// The account's data sources grouped by the kind of system they are — orders
// (ERP / OMS / shops), warehouse (WMS), transport (TMS / tracking) — one card
// per kind, one row per source: when it is read in the ticket journey, what it
// is looked up with, whether an AI model is involved, and its health from the
// API (state, reads found over 7 days, failures, last success, failing since).
// Built-in sources (Ship24, the account's orders, Spacefill, Envoi du Net) are
// listed too: nothing reads a ticket without the page saying so.
// Backend: GET /api/setup/journey → sources (state, reads_7d, found_7d) joined
// by key with GET /api/integrations/connectors → connectors (health, stats:
// failed_7d, last_success_at; legacy + upgrade). A legacy source is converted
// with « Convertir » behind a confirmation (ConvertLegacy.js, POST /migrate/*).

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ArrowRight, Boxes, Globe, Plug, Plus, Puzzle, Server, ShoppingCart, Store, Tag, Truck,
} from 'lucide-react';
import { Badge, Card, IconChip, Meter } from '../../design/ui';
import {
    connectorsByKey, formatCount, formatPercent, formatRelative, formatShortDay, formatStamp, reasonText, sourceStats, verificationEnd,
} from '../dataToolsModel';
import ConvertLegacyButton, { canConvert } from './ConvertLegacy';
import './Journey.css';

export const CATEGORIES = [
    { key: 'orders', icon: ShoppingCart, title: 'Commandes — ERP, OMS, boutiques', hint: 'La commande, ses produits et ses colis : lus avec la référence de commande, le numéro de suivi ou l’e-mail du client.' },
    { key: 'warehouse', icon: Boxes, title: 'Entrepôt — WMS', hint: 'Préparation, stock et expédition : lus avec la référence de commande ou le numéro de suivi.' },
    { key: 'transport', icon: Truck, title: 'Transport et suivi — TMS, API transporteur', hint: 'Le parcours du colis : lu avec le numéro de suivi.' },
    { key: 'other', icon: Puzzle, title: 'Autres systèmes', hint: 'Les sources qui ne rentrent dans aucune des familles ci-dessus.' },
];

export const categoryTitle = (key, t) => t(`dataSources.category.${key}.title`, (CATEGORIES.find((c) => c.key === key) || {}).title || key);

const WHEN_TEXT = {
    arrival: 'À l’arrivée de chaque ticket',
    arrival_some: 'À l’arrivée (Outlook EffiTrace, API, Spacefill)',
    arrival_email: 'À l’arrivée des e-mails',
    arrival_outlook: 'À l’arrivée des e-mails Outlook',
    arrival_edn: 'À l’arrivée des tickets Envoi du Net',
    agent: 'Quand l’agent l’interroge',
    schedule: 'Relue régulièrement',
    tracking: 'Suivi : arrivée des e-mails puis toutes les 12 h',
    incidents: 'À chaque incident Spacefill',
};

const LOOKUP_TEXT = {
    tracking_number: 'n° de suivi', order_number: 'n° de commande', customer_email: 'e-mail du client',
    invoice_number: 'n° de facture', client_ref: 'réf. client', client_name: 'client B2B', skus: 'références produit',
};

export const STATE_TEXT = { ok: 'Active', off: 'Désactivée', failing: 'En échec', verification: 'En vérification' };
export const STATE_TONE = { ok: 'success', off: undefined, failing: 'danger', verification: 'info' };

const BUILTIN_TITLES = {
    commerce_orders: 'Vos commandes (boutiques et API commandes)',
    ship24: 'Ship24 — suivi transporteur',
    edn_shipments: 'Envoi du Net — expéditions',
    spacefill: 'Spacefill',
};

export const sourceTitle = (source, t) => (BUILTIN_TITLES[source.key]
    ? t(`dataSources.builtin.${source.key}`, BUILTIN_TITLES[source.key])
    : source.title || source.key);

const kindIcon = (source) => {
    if (source.kind === 'orders') return Store;
    if (source.kind === 'brand_api') return Tag;
    if (source.kind === 'platform' || source.kind === 'builtin') return Truck;
    if (source.transport === 'browser_recipe' || source.transport === 'http_form') return Globe;
    if (source.transport === 'soap') return Server;
    return Plug;
};

const systemText = (source, t) => {
    if (source.kind === 'orders') {
        return t('dataSources.ordersSub', '{{shops}} boutique(s) · {{keys}} clé(s) API commandes', { shops: source.shops || 0, keys: source.api_keys || 0 });
    }
    if (source.kind === 'brand_api') return t('dataSources.system.brand', 'API de suivi de marque');
    if (source.kind === 'legacy') return t('dataToolsUi.sources.legacy', 'Ancienne version de source');
    return {
        http_json: t('dataSources.system.api', 'API'),
        http_form: t('dataSources.system.web', 'Site web'),
        soap: 'SOAP',
        browser_recipe: t('dataSources.system.portal', 'Portail (parcours)'),
    }[source.transport] || null;
};

const SourceRow = ({ source, connector, t, onOpen, onUpgraded, onUpgradeError }) => {
    let action = null;
    if (source.editable) {
        action = (
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => onOpen(source.key)}>
                {t('dataSources.open', 'Ouvrir')} <ArrowRight aria-hidden="true" />
            </button>
        );
    } else if (canConvert(connector)) {
        action = <ConvertLegacyButton connector={{ ...connector, title: connector.title || source.title }} onConverted={onUpgraded} onError={onUpgradeError} />;
    } else if (source.route) {
        action = (
            <Link className="u-btn u-btn--ghost u-btn--sm" to={source.route}>
                {t('dataSources.manage', 'Gérer')} <ArrowRight aria-hidden="true" />
            </Link>
        );
    }

    const builtin = ['platform', 'orders', 'builtin'].includes(source.kind);
    const stats = sourceStats(source, connector);
    const health = connector?.health;
    const failing = source.state === 'failing' || health?.status === 'failing';
    const verificationEndsAt = source.state === 'verification' ? verificationEnd(connector?.verification) : null;
    const when = (source.when || []).map((w) => t(`dataSources.when.${w}`, WHEN_TEXT[w] || w));
    const lookup = (source.lookup || []).map((l) => t(`dataSources.lookup.${l}`, LOOKUP_TEXT[l] || l)).join(', ');
    const ai = source.ai === 'repair'
        ? t('dataSources.ai.repair', 'IA seulement si la page change')
        : t('dataSources.ai.none', 'Sans IA');
    const system = systemText(source, t);
    const howRead = [
        lookup ? t('dataToolsUi.sources.lookup', 'Par {{fields}}', { fields: lookup }) : null,
        ai,
        system,
    ].filter(Boolean).join(' · ');

    return (
        <li className={`u-row sbc-row${failing ? ' sbc-row--failing' : ''}`}>
            <IconChip icon={kindIcon(source)} tone={failing ? 'danger' : 'neutral'} />
            <div className="u-row__main">
                <div className="u-row__title">
                    <span className="sbc-name">{sourceTitle(source, t)}</span>
                    <Badge tone={STATE_TONE[source.state]} dot>{t(`dataSources.srcState.${source.state}`, STATE_TEXT[source.state] || source.state)}</Badge>
                    {builtin && <Badge>{t('dataSources.builtinTag', 'inclus')}</Badge>}
                </div>
                <div className="u-row__meta" title={when.join(' · ')}>{when.join(' · ') || '—'}</div>
                <div className="u-row__meta" title={howRead}>{howRead}</div>
                {failing && health?.status === 'failing' && (
                    <div className="u-row__meta u-row__meta--wrap sbc-fail">
                        {reasonText(health.last_reason, t)}
                        {health.first_failure_at && ` ${t('dataToolsUi.sources.failingSince', 'En échec depuis le {{date}}', { date: formatStamp(health.first_failure_at) })}`}
                        {health.consecutive ? ` · ${t('dataToolsUi.sources.consecutive', '{{count}} échecs de suite', { count: health.consecutive })}` : ''}
                    </div>
                )}
                {verificationEndsAt && (
                    <div className="u-row__meta">
                        {t('dataToolsUi.sources.verificationUntil', 'Comparée à « {{legacy}} » jusqu’au {{date}}', { legacy: connector.shadow_of, date: formatShortDay(verificationEndsAt.toISOString()) })}
                    </div>
                )}
            </div>
            <div className="sbc-stats">
                {stats ? (
                    <>
                        <span className="sbc-stats__value">
                            <span className="u-num">{formatCount(stats.found)} / {formatCount(stats.reads)}</span>
                            <span className="sbc-stats__label">{t('dataToolsUi.sources.foundRead', 'trouvées / lues · 7 j')}</span>
                        </span>
                        {stats.reads > 0 && (
                            <Meter value={stats.found} max={stats.reads} tone={stats.found / stats.reads < 0.5 ? 'warning' : 'success'} label={t('dataToolsUi.sources.foundRate', 'Commandes trouvées')} />
                        )}
                        <span className="sbc-stats__sub">
                            {stats.reads > 0
                                ? [
                                    t('dataToolsUi.sources.foundShare', '{{rate}} trouvées', { rate: formatPercent(stats.found / stats.reads) }),
                                    stats.failed ? t('dataToolsUi.sources.failed', '{{count}} échecs', { count: stats.failed }) : null,
                                ].filter(Boolean).join(' · ')
                                : t('dataToolsUi.sources.noReads', 'Aucune lecture sur 7 jours')}
                        </span>
                        {stats.lastSuccessAt && (
                            <span className="sbc-stats__sub" title={formatStamp(stats.lastSuccessAt)}>{t('dataToolsUi.sources.lastSuccess', 'Dernier succès : {{when}}', { when: formatRelative(stats.lastSuccessAt) })}</span>
                        )}
                    </>
                ) : (
                    <span className="sbc-stats__sub">{t('dataToolsUi.sources.noStatsBuiltin', 'Lectures non comptées')}</span>
                )}
            </div>
            <div className="u-row__side sbc-action">{action}</div>
        </li>
    );
};

const SourcesByCategory = ({ sources, connectors, canEdit, onAdd, onOpen, onUpgraded, onUpgradeError }) => {
    const { t } = useTranslation();
    const byKey = connectorsByKey(connectors);
    return (
        <div className="sbc">
            {CATEGORIES.map((category) => {
                const rows = (sources || []).filter((s) => (s.category || 'other') === category.key);
                if (category.key === 'other' && rows.length === 0) return null;
                return (
                    <Card
                        key={category.key}
                        icon={category.icon}
                        title={categoryTitle(category.key, t)}
                        description={t(`dataSources.category.${category.key}.hint`, category.hint)}
                        actions={canEdit ? (
                            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onAdd(category.key)}>
                                <Plus aria-hidden="true" /> {t('dataSources.addIn', 'Ajouter')}
                            </button>
                        ) : null}
                        flush
                    >
                        {rows.length === 0 ? (
                            <p className="sbc-empty">{t(`dataSources.category.${category.key}.empty`, 'Aucune source de ce type.')}</p>
                        ) : (
                            <ul className="u-list">
                                {rows.map((source) => (
                                    <SourceRow key={source.key} source={source} connector={byKey[source.key]} t={t}
                                        onOpen={onOpen} onUpgraded={onUpgraded} onUpgradeError={onUpgradeError} />
                                ))}
                            </ul>
                        )}
                    </Card>
                );
            })}
        </div>
    );
};

export default SourcesByCategory;
